import React, { forwardRef } from 'react'
import { twMerge } from 'tailwind-merge'

import { Icon as GsIcon } from '../../_gluestack/icon'
import { type IconProps } from './Icon.types'

/**
 * Thin wrapper over the Gluestack `Icon`. The AOS line icons are fill-based
 * (`fill='currentColor'`), but Gluestack's `PrimitiveIcon` defaults `stroke` to
 * `currentColor` and paints it too — thickening the glyph. We force
 * `stroke='transparent'` so only the fill shows. Tint the fill with a `text-*`
 * className (`currentColor`); `size` takes an alias or a raw pixel number.
 *
 * `shrink-0` is forced because a web flex item defaults to `flex-shrink: 1`, so
 * a long enough sibling label squeezes the glyph to zero width (AOS-1228). It
 * replaces the web-only wrapper `Box` that used to carry it: nativewind applies
 * the className to the SVG on web as well, so the wrapper repeated every layout
 * utility a caller passed — `ml-1` rendered as 8px, `mb-5` as 40px. On native
 * `flexShrink` already defaults to 0, so this changes nothing there.
 */
export const Icon = forwardRef<React.ComponentRef<typeof GsIcon>, IconProps>(
    ({ as: asProp, source, size, className, ...rest }, ref) => (
        <GsIcon
            ref={ref}
            as={asProp ?? source}
            size={size}
            stroke='transparent'
            className={twMerge('shrink-0', className)}
            {...rest}
        />
    ),
)

Icon.displayName = 'Icon'
